import TextWithTooltip from 'components/ui/TextWithTooltip/TextWithTooltip';
import {
  TechnicalSkillSortField,
  TechnicalSkillWithCompanyFragment,
} from 'generated/graphql';
import { GridCellParams, GridValidRowModel } from '@spotted-zebra-uk/ui';
import { Button } from '@spotted-zebra-uk/ui-components';
import styles from './TechnicalSkillsList.module.scss';

export type Pagination = {
  page: number;
  pageSize: number;
};

export type TechnicalSkillFilters = {
  skillCategoryNames: string[];
};

export const DEFAULT_TABLE_PAGE_SIZE = 20;

export enum TechnicalSkillTableColumns {
  Name = 'Name',
  Category = 'Category',
  Description = 'Description',
  Ownership = 'Ownership',
  Actions = 'Actions',
}

export const getTableSkillSortField = (
  tableColumn: TechnicalSkillTableColumns
) => {
  switch (tableColumn) {
    case TechnicalSkillTableColumns.Name:
      return TechnicalSkillSortField.Name;
    default:
      return null;
  }
};

export const getSelectOption = (category: string) => {
  return {
    label: category,
    value: category,
  };
};

const OWNER_PLACEHOLDER = 'Global';
const DESCRIPTION_PLACEHOLDER = 'Missing description';
export const COLUMNS = [
  {
    field: TechnicalSkillTableColumns.Name,
    headerName: 'Name',
    width: 200,
    flex: 2,
    sortable: true,
    renderCell: ({
      value,
    }: GridCellParams<
      GridValidRowModel,
      { name: string; deletedAt: string | null }
    >) => {
      if (value?.deletedAt) {
        return <span className={styles.disabledCell}>{value.name}</span>;
      }
      return <span>{value?.name}</span>;
    },
  },
  {
    field: TechnicalSkillTableColumns.Category,
    headerName: 'Category',
    width: 100,
    flex: 1,
    sortable: false,
    renderCell: ({ value }: GridCellParams<GridValidRowModel, string>) => {
      return (
        <div className={styles.categoryTableCellWrapper}>
          <TextWithTooltip text={value || ''} />
        </div>
      );
    },
  },
  {
    field: TechnicalSkillTableColumns.Description,
    headerName: 'Description',
    width: 200,
    flex: 2,
    sortable: false,
    renderCell: ({
      value,
    }: GridCellParams<
      GridValidRowModel,
      { description: string; deletedAt: string }
    >) => {
      if (value?.deletedAt || !value?.description) {
        return (
          <span className={styles.disabledCell}>
            {value?.description || DESCRIPTION_PLACEHOLDER}
          </span>
        );
      }
      return <span>{String(value.description)}</span>;
    },
  },
  {
    field: TechnicalSkillTableColumns.Ownership,
    headerName: 'Ownership',
    width: 100,
    flex: 1,
    sortable: false,
    renderCell: ({
      value,
    }: GridCellParams<
      GridValidRowModel,
      { companyName: string; deletedAt: string }
    >) => {
      if (value?.deletedAt || !value?.companyName) {
        return (
          <span className={styles.disabledCell}>
            {value?.companyName ?? OWNER_PLACEHOLDER}
          </span>
        );
      }
      return <span>{String(value.companyName)}</span>;
    },
  },
  {
    field: TechnicalSkillTableColumns.Actions,
    headerName: '',
    width: 100,
    flex: 1,
    sortable: false,
    renderCell: ({
      value,
    }: GridCellParams<
      GridValidRowModel,
      {
        technicalSkillSubId: string;
        editCallback: (technicalSkillSubId: string) => void;
      }
    >) => {
      return (
        <div className={styles.editButtonContainer}>
          <Button
            size="small"
            variant="secondary"
            onClick={() => value?.editCallback(value.technicalSkillSubId)}
          >
            Edit
          </Button>
        </div>
      );
    },
  },
];

export const parseRow = (
  {
    id,
    subId,
    name,
    skillCategory,
    company,
    description,
    deletedAt,
  }: TechnicalSkillWithCompanyFragment,
  editCallback: (technicalSkillSubId: string) => void
) => {
  return {
    id: id,
    subId: subId,
    [TechnicalSkillTableColumns.Name]: { name, deletedAt },
    [TechnicalSkillTableColumns.Category]: skillCategory?.name,
    [TechnicalSkillTableColumns.Description]: { description, deletedAt },
    [TechnicalSkillTableColumns.Ownership]: {
      companyName: company?.name,
      deletedAt: deletedAt,
    },
    [TechnicalSkillTableColumns.Actions]: {
      technicalSkillSubId: subId,
      editCallback,
    },
  };
};

export const SEMANTIC_SEARCH_THRESHOLD = 1.2;
